import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import {
    Building, Factory, Stethoscope, Plane, ShoppingBag,
    Truck, GraduationCap, Landmark, Briefcase, ArrowRight
} from "lucide-react";

const IndustriesSection = () => {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"]
    });

    const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
    const y = useSpring(useTransform(scrollYProgress, [0, 1], [50, -50]), springConfig);

    const industries = [
        {
            name: "Real Estate",
            icon: Building,
            gradient: "from-blue-500 to-blue-600",
            iconColor: "text-blue-600",
            bgColor: "bg-blue-50",
            image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=80",
            description: "Property management systems, CRM solutions, and automated lead generation tools.",
            stats: "50+ Projects"
        },
        {
            name: "Manufacturing",
            icon: Factory,
            gradient: "from-slate-500 to-slate-600",
            iconColor: "text-slate-600",
            bgColor: "bg-slate-50",
            image: "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800&auto=format&fit=crop&q=80",
            description: "Inventory management, production tracking, and supply chain optimization systems.",
            stats: "40+ Projects"
        },
        {
            name: "Clinics & Healthcare",
            icon: Stethoscope,
            gradient: "from-red-500 to-red-600",
            iconColor: "text-red-600",
            bgColor: "bg-red-50",
            image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&auto=format&fit=crop&q=80",
            description: "Patient management systems, appointment scheduling, and medical records digitization.",
            stats: "30+ Projects"
        },
        {
            name: "Travel Agencies",
            icon: Plane,
            gradient: "from-sky-500 to-sky-600",
            iconColor: "text-sky-600",
            bgColor: "bg-sky-50",
            image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&auto=format&fit=crop&q=80",
            description: "Booking platforms, itinerary management, and customer relationship tools.",
            stats: "25+ Projects"
        },
        {
            name: "Retail & Distributors",
            icon: ShoppingBag,
            gradient: "from-amber-500 to-amber-600",
            iconColor: "text-amber-600",
            bgColor: "bg-amber-50",
            image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&auto=format&fit=crop&q=80",
            description: "POS systems, inventory tracking, and e-commerce integration solutions.",
            stats: "60+ Projects"
        },
        {
            name: "Logistics",
            icon: Truck,
            gradient: "from-orange-500 to-orange-600",
            iconColor: "text-orange-600",
            bgColor: "bg-orange-50",
            image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&auto=format&fit=crop&q=80",
            description: "Fleet management, route optimization, and real-time tracking systems.",
            stats: "35+ Projects"
        },
        {
            name: "Schools & Institutes",
            icon: GraduationCap,
            gradient: "from-indigo-500 to-indigo-600",
            iconColor: "text-indigo-600",
            bgColor: "bg-indigo-50",
            image: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=800&auto=format&fit=crop&q=80",
            description: "Learning management systems, student portals, and administrative automation.",
            stats: "45+ Projects"
        },
        {
            name: "Finance & Insurance",
            icon: Landmark,
            gradient: "from-emerald-500 to-emerald-600",
            iconColor: "text-emerald-600",
            bgColor: "bg-emerald-50",
            image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=80",
            description: "Portfolio management, claims processing, and automated compliance tools.",
            stats: "20+ Projects"
        }
    ];

    const topRow = industries.slice(0, 4);
    const bottomRow = industries.slice(4, 8);

    const IndustryCard = ({ industry, index, direction }) => {
        const IconComponent = industry.icon;

        return (
            <motion.div
                className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
                initial={{ opacity: 0, x: direction === 'left' ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -12, scale: 1.02 }}
            >
                {/* Animated glow */}
                <motion.div
                    className={`absolute -inset-1 bg-gradient-to-r ${industry.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-40 transition-opacity duration-500`}
                />

                {/* Image Section */}
                <div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    <motion.img
                        src={industry.image}
                        alt={industry.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-40 group-hover:opacity-30 transition-opacity`} />

                    {/* Icon Badge */}
                    <motion.div
                        className={`absolute top-6 right-6 w-16 h-16 rounded-2xl ${industry.bgColor} backdrop-blur-md flex items-center justify-center shadow-xl border border-white/50`}
                        whileHover={{ scale: 1.15, rotate: 10 }}
                        transition={{ type: "spring", stiffness: 400 }}
                    >
                        <IconComponent className={`w-8 h-8 ${industry.iconColor}`} strokeWidth={2.5} />
                    </motion.div>
                </div>

                {/* Content Section */}
                <div className="relative p-6 bg-white">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all">
                        {industry.name}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                        {industry.description}
                    </p>


                </div>

                {/* Decorative corner */}
                <div
                    className={`absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl ${industry.gradient} opacity-5 group-hover:opacity-10 transition-opacity`}
                    style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }}
                />
            </motion.div>
        );
    };

    return (
        <section ref={sectionRef} className="relative py-20 overflow-hidden bg-white border-t border-gray-200">
            {/* Background grid */}
            <motion.div
                style={{ y, opacity: 0.05 }}
                className="absolute inset-0"
            >
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                        <pattern id="industries-grid" width="8" height="8" patternUnits="userSpaceOnUse">
                            <path d="M 8 0 L 0 0 0 8" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-300" />
                        </pattern>
                    </defs>
                    <rect width="100" height="100" fill="url(#industries-grid)" />
                </svg>
            </motion.div>

            {/* Floating orbs */}
            <motion.div
                style={{ y: useTransform(scrollYProgress, [0, 1], [0, 100]) }}
                className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                style={{ y: useTransform(scrollYProgress, [0, 1], [0, -100]) }}
                className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="container mx-auto 3xl:max-w-[1800px] relative z-10">
                {/* Section Header */}
                <motion.div
                    className="text-center max-w-3xl mx-auto"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <motion.div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 mb-4 md:mb-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                    >
                        <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        </motion.div>
                        <span className="text-xs sm:text-sm font-semibold text-gray-700">Industries</span>
                    </motion.div>

                    <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal py-1">
                        Industries We Serve
                    </h2>
                    <p className="text-xl text-slate-600 leading-relaxed mb-10">
                        Delivering tailored digital solutions designed to meet the unique needs of every industry we work with.
                    </p>
                </motion.div>

                {/* Top Row - Left to Right */}
                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-8"
                    initial={{ opacity: 0, x: -100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    {topRow.map((industry, index) => (
                        <IndustryCard key={index} industry={industry} index={index} direction="left" />
                    ))}
                </motion.div>

                {/* Bottom Row - Right to Left */}
                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
                    initial={{ opacity: 0, x: 100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                >
                    {bottomRow.map((industry, index) => (
                        <IndustryCard key={index} industry={industry} index={index} direction="right" />
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default IndustriesSection;
